import * as React from "react";
import { useSelector } from "react-redux";
import { Divider } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { LinkObject } from "@teamprevent/common/model";

import { OrgStructureDetailModel, OrgStructuresModel } from "../models";

export interface Props {
    onOrgStructuresModelChange: (model: OrgStructuresModel) => void;
    initialModel?: OrgStructuresModel;
    submitted?: boolean;
    loadOrgStructure: (parentOrgStructureLevelId: string) => Promise<void>;
    customerId: string;
}

const NoneOption: { text: string;  value: string} = { text: "None", value: "" };

export const OrgStructures: React.FunctionComponent<Props> = ({ customerId, loadOrgStructure, onOrgStructuresModelChange, ...props }) => {

    const allOrgStructures = useSelector((state: { orgStructures: OrgStructureDetailModel[] }) => state.orgStructures);
    const orgStructuresForCustomer = React.useMemo(() => allOrgStructures.filter(o => o.customerId === customerId), [allOrgStructures, customerId]);

    const [model, setModel] = React.useState<OrgStructuresModel | undefined>({
        orgStructureOne: undefined,
        orgStructureTwo: undefined,
        orgStructureThree: undefined,
        orgStructureFour: undefined
    });

    const [selectedOrgStructureLevelIds, setSelectedOrgStructureLevelIds] = React.useState<string[]>([]);

    React.useEffect(() => {
        loadOrgStructure("");
    }, [loadOrgStructure]);

    React.useEffect(() => {
        setModel(props.initialModel);

        let selectedOrgIds: string[] = [];
        if (props.initialModel?.orgStructureOne?.id) {
            selectedOrgIds = [props.initialModel?.orgStructureOne?.id];
        }
        if (props.initialModel?.orgStructureTwo?.id) {
            selectedOrgIds = [...selectedOrgIds, props.initialModel?.orgStructureTwo?.id];
        }
        if (props.initialModel?.orgStructureThree?.id) {
            selectedOrgIds = [...selectedOrgIds, props.initialModel?.orgStructureThree?.id];
        }
        if (props.initialModel?.orgStructureFour?.id) {
            selectedOrgIds = [...selectedOrgIds, props.initialModel?.orgStructureFour?.id];
        }
        setSelectedOrgStructureLevelIds(selectedOrgIds);
    }, [props.initialModel]);

    React.useEffect(() => {
        const selectedId = model?.orgStructureOne?.id;
        if (selectedId) {
            loadOrgStructure(selectedId);
        }
    }, [loadOrgStructure, model?.orgStructureOne?.id]);

    React.useEffect(() => {
        const selectedId = model?.orgStructureTwo?.id;
        if (selectedId) {
            loadOrgStructure(selectedId);
        }
    }, [loadOrgStructure, model?.orgStructureTwo?.id]);

    React.useEffect(() => {
        const selectedId = model?.orgStructureThree?.id;
        if (selectedId) {
            loadOrgStructure(selectedId);
        }
    }, [loadOrgStructure, model?.orgStructureThree?.id]);

    React.useEffect(() => {
        const selectedId = model?.orgStructureFour?.id;
        if (selectedId) {
            loadOrgStructure(selectedId);
        }
    }, [loadOrgStructure, model?.orgStructureFour?.id]);

    const orgStructureLevelOneOptions = React.useMemo(() => {
        const filteredOrgStructures = orgStructuresForCustomer?.filter(orgLevel => !orgLevel.parentId);

        return filteredOrgStructures?.map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) ?? [];
    }, [orgStructuresForCustomer]);

    const orgStructureLevelTwoOptions = React.useMemo(() => {
        if (selectedOrgStructureLevelIds[0] === NoneOption.value) {
            return [];
        }

        const options = orgStructuresForCustomer.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[0])?.
            map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) ?? [];

        if (options.length > 0) {
            return [NoneOption, ...options];
        }

        return options;
    }, [orgStructuresForCustomer, selectedOrgStructureLevelIds]);

    const orgStructureLevelThreeOptions = React.useMemo(() => {
        if (selectedOrgStructureLevelIds[1] === NoneOption.value) {
            return [];
        }

        const options = orgStructuresForCustomer.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[1])?.
            map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) ?? [];

        if (options.length > 0) {
            return [NoneOption, ...options];
        }

        return options;
    }, [orgStructuresForCustomer, selectedOrgStructureLevelIds]);

    const orgStructureLevelFourOptions = React.useMemo(() => {
        if (selectedOrgStructureLevelIds[2] === NoneOption.value) {
            return [];
        }

        const options =  orgStructuresForCustomer.filter(orgLevel => orgLevel.parentId === selectedOrgStructureLevelIds[2])?.
            map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) ?? [];

        if (options.length > 0) {
            return [NoneOption, ...options];
        }

        return options;
    }, [orgStructuresForCustomer, selectedOrgStructureLevelIds]);

    const getOrgStructureLink = React.useCallback((options: { text: string; value: string }[], selectedId: string): LinkObject | null => {
        const selectedOrgLevel = options?.find(o => o.value === selectedId);
        if (selectedId === "")
        {
            return { id: NoneOption.value, name: NoneOption.text };
        }

        if (!selectedOrgLevel) {
            return null;
        }
        return { id: selectedOrgLevel.value, name: selectedOrgLevel.text };
    }, []);

    const onOrgStructureLevelOneChange = React.useCallback(async (selectedId: string) => {
        if (selectedId && selectedId !== model?.orgStructureOne?.id) {
            setSelectedOrgStructureLevelIds([...selectedOrgStructureLevelIds.slice(0, 0), selectedId]);
            const orgStructureLink = getOrgStructureLink(orgStructureLevelOneOptions, selectedId);
            if (!orgStructureLink) {
                return;
            }

            const updatedModel: OrgStructuresModel = {
                ...model,
                orgStructureOne: orgStructureLink,
                orgStructureTwo: undefined,
                orgStructureThree: undefined,
                orgStructureFour: undefined };

            setModel(updatedModel);
            onOrgStructuresModelChange(updatedModel);

            await loadOrgStructure(selectedId);
        }
    }, [model, selectedOrgStructureLevelIds, getOrgStructureLink, orgStructureLevelOneOptions, onOrgStructuresModelChange, loadOrgStructure]);

    const onOrgStructureLevelTwoChange = React.useCallback(async (selectedId: string) => {
        if (selectedId !== model?.orgStructureTwo?.id) {
            setSelectedOrgStructureLevelIds([...selectedOrgStructureLevelIds.slice(0, 1), selectedId]);
            const orgStructureLink = getOrgStructureLink(orgStructureLevelTwoOptions, selectedId);
            if (!orgStructureLink) {
                return;
            }

            const updatedModel: OrgStructuresModel = { ...model, orgStructureTwo: orgStructureLink, orgStructureThree: undefined, orgStructureFour: undefined };
            setModel(updatedModel);

            if (selectedId) {
                await loadOrgStructure(selectedId);
            }

            onOrgStructuresModelChange(updatedModel);
        }
    }, [model, selectedOrgStructureLevelIds, getOrgStructureLink, orgStructureLevelTwoOptions, onOrgStructuresModelChange, loadOrgStructure]);

    const onOrgStructureLevelThreeChange = React.useCallback(async (selectedId: string) => {
        if (selectedId !== model?.orgStructureThree?.id) {
            setSelectedOrgStructureLevelIds([...selectedOrgStructureLevelIds.slice(0, 2), selectedId]);
            const orgStructureLink = getOrgStructureLink(orgStructureLevelThreeOptions, selectedId);
            if (!orgStructureLink) {
                return;
            }

            const updatedModel: OrgStructuresModel = { ...model, orgStructureThree: orgStructureLink, orgStructureFour: undefined };
            setModel(updatedModel);

            if (selectedId) {
                await loadOrgStructure(selectedId);
            }
            onOrgStructuresModelChange(updatedModel);
        }
    }, [model, selectedOrgStructureLevelIds, getOrgStructureLink, orgStructureLevelThreeOptions, onOrgStructuresModelChange, loadOrgStructure]);

    const onOrgStructureLevelFourChange = React.useCallback(async (selectedId: string) => {
        if (selectedId !== model?.orgStructureFour?.id) {
            setSelectedOrgStructureLevelIds([...selectedOrgStructureLevelIds.slice(0, 3), selectedId]);
            const orgStructureLink = getOrgStructureLink(orgStructureLevelFourOptions, selectedId);
            if (!orgStructureLink) {
                return;
            }

            const updatedModel: OrgStructuresModel = { ...model, orgStructureFour: orgStructureLink };
            setModel(updatedModel);

            if (selectedId) {
                await loadOrgStructure(selectedId);
            }

            onOrgStructuresModelChange(updatedModel);
        }
    }, [model, selectedOrgStructureLevelIds, getOrgStructureLink, orgStructureLevelFourOptions, onOrgStructuresModelChange, loadOrgStructure]);

    return (
        <>
            <Input.Dropdown
                label="Organisation Level One *"
                placeholder="Please select"
                dynamicOptions={true}
                value={model?.orgStructureOne?.id}
                showErrors={props.submitted}
                options={orgStructureLevelOneOptions}
                onChange={onOrgStructureLevelOneChange}
                required
                search={true}
            />
            <Divider hidden />
            <Input.Dropdown
                label="Organisation Level Two"
                placeholder="Please select"
                dynamicOptions={true}
                value={model?.orgStructureTwo?.id}
                showErrors={props.submitted}
                options={orgStructureLevelTwoOptions}
                onChange={onOrgStructureLevelTwoChange}
                search={true}
            />
            <Divider hidden />
            <Input.Dropdown
                label="Organisation Level Three"
                placeholder="Please select"
                dynamicOptions={true}
                value={model?.orgStructureThree?.id}
                showErrors={props.submitted}
                options={orgStructureLevelThreeOptions}
                onChange={onOrgStructureLevelThreeChange}
                search={true}
            />
            <Divider hidden />
            <Input.Dropdown
                label="Organisation Level Four"
                placeholder="Please select"
                dynamicOptions={true}
                value={model?.orgStructureFour?.id}
                showErrors={props.submitted}
                options={orgStructureLevelFourOptions}
                onChange={onOrgStructureLevelFourChange}
                search={true}
            />
        </>
    );
};
